import React, { useState } from 'react';
import cx from 'classnames';
import { PROVIDER_LOGOS } from 'components/providerTicker/providerTicker';
import { formatProviderName } from 'utils/formatProviderName';
import styles from './ProviderLogo.module.scss';

export interface ProviderLogoProps {
  name?: string;
  displayName?: string;
  imageUrl?: string;
  className?: string;
  textClassName?: string;
}

const isLikelyUrl = (url?: string | null): boolean => {
  if (!url) return false;
  const trimmed = url.trim();
  return (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('data:image/') ||
    /\.(png|jpe?g|svg|webp|gif|avif)(\?.*)?$/i.test(trimmed)
  );
};

export const ProviderLogo: React.FC<ProviderLogoProps> = ({
  name = '',
  displayName,
  imageUrl,
  className,
  textClassName,
}) => {
  const [prevImageUrl, setPrevImageUrl] = useState(imageUrl);
  const [imageError, setImageError] = useState(false);

  if (imageUrl !== prevImageUrl) {
    setPrevImageUrl(imageUrl);
    setImageError(false);
  }

  // Handle case where API might pass a text slug in imageUrl instead of a link
  const rawSlugCandidate = !isLikelyUrl(imageUrl) ? imageUrl : '';
  const effectiveName = name || rawSlugCandidate || '';
  const effectiveDisplay = displayName || name || rawSlugCandidate || '';

  const cleanName = effectiveName.toLowerCase().trim();
  const normalizedKey = cleanName.replace(/[-_\s]+/g, '');

  const LogoComponent =
    PROVIDER_LOGOS[effectiveName] ||
    PROVIDER_LOGOS[cleanName] ||
    PROVIDER_LOGOS[normalizedKey];

  const formattedName = formatProviderName(effectiveDisplay);

  if (imageUrl && isLikelyUrl(imageUrl) && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={formattedName}
        className={cx(styles.logoImage, className)}
        onError={() => setImageError(true)}
      />
    );
  }

  if (LogoComponent) {
    return <LogoComponent className={cx(styles.logoSvg, className)} />;
  }

  return (
    <span className={styles.fallbackBadge} title={formattedName}>
      <span className={cx(styles.fallbackText, textClassName)}>{formattedName}</span>
    </span>
  );
};

export default ProviderLogo;
